import {
  LookupKeys,
  AttendeeKeys,
  AttendeeType,
  LookupId,
  RegistrationType,
  QualifiedAs,
  InviteeStatus,
  PhoneNumber,
  LocalDateTimeString,
  TShirtSizes,
  InviteeAgeGroup,
  DietaryConsideration,
  Conference,
  Registrant,
  GuestData,
  RegistrantKeys,
  RegistrantData,
  RegistrantModelKeys,
  AssociationKeys,
  GuestKeys,
  AttendeeWizardState,
  GuestStatus,
  ConferenceKeys,
  ConferencePossibleStayPeriodKey,
  HotelReservationKeys,
  HotelReservation,
  GuestEvents,
} from '@ag-common-lib/public-api.functions';
import { calculateWizardState, getCurrentStepInProgress } from './conference-registration.util';
import { BaseModelKeys } from '@ag-common-lib/lib/models/base.model';
import { ChairmanExtensionConfigurationKeys } from '@ag-common-lib/lib/models/registration/conference-chairman-extension';
import { get, set } from 'lodash';
import { RegistrantChairmanExtensionKeys } from '@ag-common-lib/lib/models/registration/registrant-chairman-extension';

export class Attendee {
  [AttendeeKeys.conferenceDbId]: string;
  [AttendeeKeys.attendeeType]: AttendeeType;
  [AttendeeKeys.stepInProgress]: any;
  [AttendeeKeys.registrationState]: AttendeeWizardState;
  [AttendeeKeys.inviteeEmail]: string;
  [AttendeeKeys.inviteeEmailType]: LookupId<'EmailTypes'>;
  [AttendeeKeys.companyReference]: string;
  [AttendeeKeys.registrationType]: RegistrationType;
  [AttendeeKeys.qualifiedAs]: QualifiedAs;
  [AttendeeKeys.mgaId]: string;
  [AttendeeKeys.agencyId]: string;
  [AttendeeKeys.agentId]: string;
  [AttendeeKeys.inviteeStatus]: InviteeStatus;
  [AttendeeKeys.inviteeOutcomeStatus]: InviteeStatus;
  [AttendeeKeys.phoneNumber]?: PhoneNumber;

  [AttendeeKeys.prefix]?: LookupId<'Prefixes'>;
  [AttendeeKeys.firstName]: string;
  [AttendeeKeys.middleName]?: string;
  [AttendeeKeys.lastName]: string;
  [AttendeeKeys.suffix]?: LookupId<'Suffixes'>;
  [AttendeeKeys.gender]?: LookupId<'Genders'>;
  [AttendeeKeys.dob]?: LocalDateTimeString;

  [AttendeeKeys.badgeName]?: string;
  [AttendeeKeys.awardName]?: string;
  [AttendeeKeys.tShirtSizes]?: TShirtSizes;
  [AttendeeKeys.childOrAdult]?: InviteeAgeGroup = null;
  [AttendeeKeys.firstTimeAttendee]?: boolean = false;

  [AttendeeKeys.dietaryConsideration]?: DietaryConsideration;
  [AttendeeKeys.associationType]?: LookupId<'AssociationType'>;

  constructor(
    conference: Conference,
    readonly registrant: Registrant,
    readonly guest: GuestData = null,
  ) {
    // Computed once and handed to calculateWizardState, which would otherwise redo the same work.
    // One Attendee is built per grid row, so this is the difference between one and two passes
    // of getCurrentStepInProgress for every registrant in the conference.
    const stepInProgress = getCurrentStepInProgress({ conference, conferenceRegistration: registrant });

    this[AttendeeKeys.stepInProgress] = stepInProgress;
    this[AttendeeKeys.registrationState] = calculateWizardState(registrant, conference, stepInProgress);

    this.init();
  }

  private init() {
    if (this.guest) {
      this.getAttendeeFromGuest();
      return;
    }

    this.getAttendeeFromRegistrant();
  }

  private getAttendeeFromRegistrant() {
    this[AttendeeKeys.dbId] = this.registrant?.[BaseModelKeys.dbId];
    this[AttendeeKeys.conferenceDbId] = this.registrant?.[RegistrantKeys.conferenceDbId];
    this[AttendeeKeys.attendeeType] = AttendeeType.Invitee;
    this[AttendeeKeys.childOrAdult] = InviteeAgeGroup.adult;

    const registrantData: RegistrantData = this.registrant?.[RegistrantModelKeys.data];

    const registrantToAttendeeMap = new Map<RegistrantKeys | string[], AttendeeKeys>([
      [RegistrantKeys.prefix, AttendeeKeys.prefix],
      [RegistrantKeys.firstName, AttendeeKeys.firstName],
      [RegistrantKeys.middleName, AttendeeKeys.middleName],
      [RegistrantKeys.lastName, AttendeeKeys.lastName],
      [RegistrantKeys.suffix, AttendeeKeys.suffix],
      [RegistrantKeys.gender, AttendeeKeys.gender],
      [RegistrantKeys.dob, AttendeeKeys.dob],
      [RegistrantKeys.dietaryConsideration, AttendeeKeys.dietaryConsideration],
      [RegistrantKeys.badgeName, AttendeeKeys.badgeName],
      [RegistrantKeys.tShirtSizes, AttendeeKeys.tShirtSizes],
      [RegistrantKeys.agentId, AttendeeKeys.agentId],
      [RegistrantKeys.inviteeOutcomeStatus, AttendeeKeys.inviteeOutcomeStatus],
      [RegistrantKeys.isApplicableForChairmanExtension, AttendeeKeys.invitedChairmanExt],
      [
        [RegistrantKeys.chairmanExtension, RegistrantChairmanExtensionKeys.inviteeStatus],
        AttendeeKeys.chairmanExtAttendingDecision,
      ],
    ]);

    this.attendeeCommonFields.forEach((attendeeKey, registrantKey) => {
      this[attendeeKey] = registrantData?.[registrantKey];
    });

    registrantToAttendeeMap.forEach((attendeeKey, registrantKey) => {
      this[attendeeKey] = get(registrantData, registrantKey);
    });
  }

  private getAttendeeFromGuest() {
    this[AttendeeKeys.dbId] = this.guest?.[BaseModelKeys.dbId];
    this[AttendeeKeys.attendeeType] = AttendeeType.Guest;

    const registrantData: RegistrantData = this.registrant?.[RegistrantModelKeys.data];

    const guestToAttendeeMap = new Map<AssociationKeys | GuestKeys, AttendeeKeys>([
      [AssociationKeys.firstName, AttendeeKeys.firstName],
      [AssociationKeys.middleName, AttendeeKeys.middleName],
      [AssociationKeys.lastName, AttendeeKeys.lastName],
      [AssociationKeys.prefix, AttendeeKeys.prefix],
      [AssociationKeys.suffix, AttendeeKeys.suffix],
      [AssociationKeys.gender, AttendeeKeys.gender],
      [AssociationKeys.dob, AttendeeKeys.dob],
      [AssociationKeys.dietaryConsideration, AttendeeKeys.dietaryConsideration],
      [AssociationKeys.associationType, AttendeeKeys.associationType],
      [GuestKeys.badgeName, AttendeeKeys.badgeName],
      [AssociationKeys.contactNumber, AttendeeKeys.phoneNumber],
      [AssociationKeys.tShirtSizes, AttendeeKeys.tShirtSizes],
      [GuestKeys.childOrAdult, AttendeeKeys.childOrAdult],
    ]);

    this.attendeeCommonFields.forEach((attendeeKey, registrantKey) => {
      this[attendeeKey] = registrantData?.[registrantKey];
    });

    const guestStatus = this.guest?.[GuestKeys.guestStatus];

    this[AttendeeKeys.inviteeOutcomeStatus] =
      guestStatus === GuestStatus.canceled
        ? InviteeStatus.cancelled
        : this.registrant?.[RegistrantModelKeys.data]?.[RegistrantKeys.inviteeOutcomeStatus];

    guestToAttendeeMap.forEach((attendeeKey, guestKey) => {
      const guestData = this.guest?.[guestKey];

      this[attendeeKey] = guestData;
    });

    const guestEvents = this.guest?.[GuestKeys.guestEvents];
    const guestEventsSet = new Set(guestEvents);

    this[AttendeeKeys.invitedChairmanExt] = guestEventsSet.has(GuestEvents.chairmanExtension) ? true : false;
    if (guestEventsSet.has(GuestEvents.chairmanExtension)) {
      this[AttendeeKeys.chairmanExtAttendingDecision] =
        guestStatus === GuestStatus.canceled ? InviteeStatus.cancelled : InviteeStatus.accepted;
    }
  }

  private attendeeCommonFields = new Map<RegistrantKeys, AttendeeKeys>([
    [RegistrantKeys.awardName, AttendeeKeys.awardName],
    [RegistrantKeys.firstTimeAttendee, AttendeeKeys.firstTimeAttendee],
    [RegistrantKeys.inviteeStatus, AttendeeKeys.inviteeStatus],
    [RegistrantKeys.inviteeEmail, AttendeeKeys.inviteeEmail],
    [RegistrantKeys.inviteeEmailType, AttendeeKeys.inviteeEmailType],
    [RegistrantKeys.mobilePhone, AttendeeKeys.phoneNumber],
    [RegistrantKeys.mgaId, AttendeeKeys.mgaId],
    [RegistrantKeys.agencyId, AttendeeKeys.agencyId],

    [RegistrantKeys.registrationType, AttendeeKeys.registrationType],
    [RegistrantKeys.qualifiedAs, AttendeeKeys.qualifiedAs],
    [RegistrantKeys.companyReference, AttendeeKeys.companyReference],
    [RegistrantKeys.emergencyContact, AttendeeKeys.emergencyContact],
  ]);

  static getRegistrantTravelDates(
    conference: Conference,
    registrationType: RegistrationType,
    needToApplyChairmanExtRange: boolean,
  ) {
    const isChairmanExtensionEnabled = conference?.[ConferenceKeys.isChairmanExtensionEnabled];
    const participantScheduleRanges = conference?.[ConferenceKeys.participantScheduleRanges];
    const participantScheduleRangesForExtension =
      conference?.[ConferenceKeys.chairmanExtensionConfiguration]?.[ChairmanExtensionConfigurationKeys.stayPeriods];
    const stayPeriods =
      isChairmanExtensionEnabled && needToApplyChairmanExtRange
        ? participantScheduleRangesForExtension
        : participantScheduleRanges;
    const scheduleRange = stayPeriods.find(
      range => range?.[ConferencePossibleStayPeriodKey.registrationType] === registrationType,
    );
    const arrivalDate =
      scheduleRange?.[ConferencePossibleStayPeriodKey.primaryArrivalDate] ?? conference?.[ConferenceKeys.startDate];
    const departureDate =
      scheduleRange?.[ConferencePossibleStayPeriodKey.primaryDepartureDate] ?? conference?.[ConferenceKeys.endDate];

    const hotelReservation: Partial<HotelReservation> = {};

    set(hotelReservation, [HotelReservationKeys.checkInDate], arrivalDate);
    set(hotelReservation, [HotelReservationKeys.checkOutDate], departureDate);
    set(hotelReservation, [HotelReservationKeys.requestedCheckInDate], arrivalDate);
    set(hotelReservation, [HotelReservationKeys.requestedCheckOutDate], departureDate);

    return hotelReservation;
  }
}
